import { api } from './api';
import { toApiError, apiErrorMessage } from './apiError';
import { cacheManager, photoImageCache } from '../utils/cache';

// api.* ApiError fırlatır; ApiError'da `.response` YOK. Eski `error.response?.data?.message`
// okumaları her zaman undefined dönüp backend'in TR mesajını jenerik metinle eziyordu.
// toApiError(error).message önce backend mesajını, yoksa hata türüne göre TR metni verir.

// Types
export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  company_id: number;
  bucket_name: string;
  last_login?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  token?: string;
  user?: User;
  error?: string;
}

class AuthService {
  private readonly TOKEN_KEY = 'deepwind-auth-token';
  private readonly USER_KEY = 'deepwind-user';

  /**
   * Token validation check
   */
  async validateToken(): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) return false;
      
      const response = await api.get<any>('/auth/verify');
      return response?.success === true;
    } catch (error) {
      // NOT: bu metodun su an src/ icinde HIC CAGIRANI YOK (ProtectedRoute
      // isTokenValid() + validateSession() kullaniyor). Sessiz kalmasi bu yuzden
      // kullaniciyi etkilemiyor; ileride cagrilirsa cagiran tarafta yuzeye cikarilmali.
      const apiError = toApiError(error);
      console.warn('Token validation failed:', apiError.message);
      // Yalniz gercek bir reddedis (401) oturumu dusurur. Onceden kosulsuz logout()
      // vardi: anlik bir ag kopmasi kullaniciyi oturumdan atardi.
      if (apiError.kind === 'auth') {
        this.logout();
      }
      return false;
    }
  }

  /**
   * Kullanıcı giriş işlemi
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      
      // Backend direkt LoginResponse döndürüyor, ApiResponse wrapper'ı yok
      const data = response as any;
      
      if (data.success && data.token && data.user) {
        // Clear cache before setting new user data to prevent role conflicts
        this.clearAllCache();
        this.setAuthData(data.token, data.user);
      }
      
      return data;
    } catch (error) {
      // Eski kod `error?.response?.data?.error` okuyordu: backend böyle bir alan hiç
      // döndürmüyor, değer HER ZAMAN 'LOGIN_ERROR' oluyordu. Artık gerçek makine-okur
      // kod (varsa) ApiError.code'dan gelir; yoksa alan hiç gönderilmez.
      const apiError = toApiError(error);
      return {
        success: false,
        message: apiError.message || 'Giriş işleminde hata oluştu',
        ...(apiError.code ? { error: apiError.code } : {})
      };
    }
  }

  /**
   * Kullanıcı çıkış işlemi
   */
  async logout(): Promise<void> {
    try {
      // Backend'e logout request gönder (isteğe bağlı)
      await api.post('/auth/logout');
    } catch {
      // Bilerek sessiz: çıkış kullanıcının İSTEDİĞİ sonuç ve finally bloğu yerel oturumu
      // her durumda temizliyor. Sunucuya haber verilememesi kullanıcının yapabileceği
      // bir şey değil; burada hata göstermek çıkışı başarısız gibi gösterirdi.
    } finally {
      // Her durumda local storage'ı ve cache'i temizle
      this.clearAuthData();
      this.clearAllCache();
    }
  }

  /**
   * Token yenileme işlemi
   */
  async refreshToken(): Promise<boolean> {
    try {
      const response = await api.post<{ success: boolean; token?: string }>('/auth/refresh');
      const data = response.data;
      
      if (data.success && data.token) {
        this.setToken(data.token);
        return true;
      }
      
      return false;
    } catch (error) {
      // NOT: bu metodun src/ icinde CAGIRANI YOK ve hedefledigi `/api/auth/refresh`
      // ucu backend'de TANIMLI DEGIL -- cagrilsaydi 404 alip kullaniciyi oturumdan
      // atardi. Oldugu gibi birakilmasi guvenli olsun diye yikici cagri kosullandirildi.
      const apiError = toApiError(error);
      if (apiError.kind === 'auth') {
        this.clearAuthData();
      }
      return false;
    }
  }

  /**
   * Mevcut kullanıcı profil bilgilerini al
   */
  async getProfile(): Promise<User | null> {
    try {
      const response = await api.get<{ success: boolean; user?: User }>('/auth/profile');
      const data = response.data;
      
      if (data.success && data.user) {
        this.setUser(data.user);
        return data.user;
      }
      
      return null;
    } catch (error) {
      // NOT: bu metodun src/ icinde CAGIRANI YOK; ekranlar yerel kullanici bilgisini
      // (getCurrentUser) kullaniyor. Bu yuzden null donusu bugun kimseye gorunmuyor.
      // Gercek oturum dusmesi (401) api.ts interceptor'inda merkezi ele aliniyor.
      console.warn('Profil alınamadı:', toApiError(error).message);
      return null;
    }
  }

  /**
   * Token'ı local storage'a kaydet
   */
  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Local storage'dan token'ı al
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Kullanıcı bilgilerini local storage'a kaydet
   */
  setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Local storage'dan kullanıcı bilgilerini al
   */
  getUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        this.clearAuthData();
      }
    }
    return null;
  }

  /**
   * Token ve kullanıcı bilgilerini kaydet
   */
  setAuthData(token: string, user: User): void {
    this.setToken(token);
    this.setUser(user);
  }

  /**
   * Tüm auth verilerini temizle
   */
  clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  /**
   * Tüm cache'i temizle (user role değişikliklerinde kullanılır)
   */
  clearAllCache(): void {
    cacheManager.clear();
    photoImageCache.clearCache();
    
    // Also clear any session storage if used
    try {
      sessionStorage.clear();
    } catch (error) {
      // Ignore sessionStorage errors
    }
  }

  /**
   * Kullanıcının giriş yapmış olup olmadığını kontrol et
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  /**
   * Token'ın geçerli olup olmadığını kontrol et
   */
  isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Local development için mock token kontrolü
    if (token === 'mock-jwt-token') {
      return true;
    }

    try {
      // JWT token'ın payload kısmını decode et
      const payload = JSON.parse(atob(token.split('.')[1]!));
      const currentTime = Math.floor(Date.now() / 1000);
      
      // Token süresi dolmuş mu kontrol et
      return payload.exp > currentTime;
    } catch (error) {
      return false;
    }
  }

  /**
   * Kullanıcının belirli bir role sahip olup olmadığını kontrol et
   */
  hasRole(role: string): boolean {
    const user = this.getUser();
    return user?.role === role;
  }

  /**
   * Kullanıcının belirli rollerden birine sahip olup olmadığını kontrol et
   */
  hasAnyRole(roles: string[]): boolean {
    const user = this.getUser();
    return user ? roles.includes(user.role) : false;
  }

  /**
   * Admin yetkisi kontrol et
   */
  isAdmin(): boolean {
    return this.hasAnyRole(['admin', 'super_admin']);
  }

  /**
   * Operatör yetkisi kontrol et
   */
  isOperator(): boolean {
    return this.hasRole('operator');
  }

  /**
   * İzleyici yetkisi kontrol et
   */
  isViewer(): boolean {
    return this.hasRole('viewer');
  }

  /**
   * Mevcut kullanıcıyı al
   */
  getCurrentUser(): User | null {
    const userData = localStorage.getItem('deepwind-user');
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        // Bozuk localStorage icerigi. `null` donmek BILINCLI sozlesmedir: bu metot
        // senkrondur ve throw ETMEZ. Cagiranlar "kullanici yok" halini kendileri
        // yuzeye cikarmalidir (bkz. CommentSidebar'lardaki userWarning).
        console.warn('Bozuk oturum verisi okunamadi:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Şifre sıfırlama talebi gönder
   */
  async requestPasswordReset(email: string): Promise<{ success: boolean; message?: string; data?: any }> {
    try {
      const response = await api.post('/auth/password-reset', { email });
      
      if (response.success) {
        return {
          success: true,
          message: response.message || 'Şifre başarıyla sıfırlandı',
          data: response.data
        };
      }
      
      return {
        success: false,
        message: response.message || 'Şifre sıfırlama işlemi başarısız'
      };
    } catch (error) {
      return {
        success: false,
        message: apiErrorMessage(error, 'Şifre sıfırlama işleminde hata oluştu')
      };
    }
  }

  /**
   * Session geçerliliğini kontrol et
   */
  async validateSession(): Promise<{
    success: boolean
    user?: User
    message?: string
    /**
     * true ise oturum GEÇERSİZ DEĞİL; sunucuya ulaşılamadı (ağ/zaman aşımı/sunucu
     * hatası). Çağıran bu durumda kullanıcıyı oturumdan ATMAMALIDIR.
     */
    transient?: boolean
  }> {
    try {
      // ONBELLEK KAPALI: api.get varsayilan olarak 5 dakika cache'liyor.
      // Oturum gecerliligi cache'den okunursa sunucuda iptal edilmis bir oturum
      // dakikalarca gecerli gorunur.
      const response = await api.get('/auth/validate-session', undefined, { useCache: false }) as any;
      
      if (response.success && response.user) {
        // Update local user data if session is valid
        this.setAuthData(this.getToken() || '', response.user);
        return {
          success: true,
          user: response.user
        };
      }
      
      // Sunucu açıkça "bu oturum geçersiz" dedi: yerel veriyi temizle.
      this.clearAuthData();
      return {
        success: false,
        message: response.message || 'Oturum geçersiz'
      };
    } catch (error) {
      const apiError = toApiError(error);

      // Geçici bir arıza oturumu geçersiz kılmaz. Önceden buradaki koşulsuz
      // clearAuthData() yüzünden anlık bir internet kopması kullanıcının
      // token'ını SİLİYOR ve onu oturumdan atıyordu.
      // Yalnız 401 (kind === 'auth') gerçek bir reddediştir.
      const isGenuineRejection = apiError.kind === 'auth';
      if (isGenuineRejection) {
        this.clearAuthData();
      }

      return {
        success: false,
        message: apiError.message,
        transient: !isGenuineRejection
      };
    }
  }
}

// Singleton instance
export const authService = new AuthService();